/**
 * Cloudinary upload utility.
 * Uses unsigned upload preset — no API secret exposed in the frontend.
 * Old Supabase URLs are kept as-is (backward compatibility).
 */

const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string;
const UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string;

export interface CloudinaryUploadResult {
  public_id: string;
  secure_url: string;
  width: number;
  height: number;
  format: string;
  bytes: number;
}

/**
 * Upload a file to Cloudinary using an unsigned preset.
 * Uses XHR to support upload progress tracking.
 *
 * @param resourceType - 'image' for photos, 'auto' for PDFs and mixed files
 */
export function uploadToCloudinary(
  file: File,
  folder: string,
  publicId: string,
  onProgress?: (percent: number) => void,
  resourceType: 'image' | 'auto' = 'image'
): Promise<CloudinaryUploadResult> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', UPLOAD_PRESET);
    formData.append('public_id', `${folder}/${publicId}`);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as CloudinaryUploadResult);
      } else {
        reject(new Error(`Cloudinary upload failed (${xhr.status}): ${xhr.responseText}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Cloudinary upload network error')));
    xhr.addEventListener('abort', () => reject(new Error('Cloudinary upload aborted')));

    xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${resourceType}/upload`);
    xhr.send(formData);
  });
}

/**
 * Check if a URL is a Cloudinary URL.
 */
export function isCloudinaryUrl(url: string): boolean {
  return url.includes('res.cloudinary.com');
}

/**
 * Extract the public_id from a Cloudinary URL.
 * Works for both image (/image/upload/) and raw (/raw/upload/) resources.
 * e.g. https://res.cloudinary.com/cloud/image/upload/v123/folder/name.jpg → folder/name
 * e.g. https://res.cloudinary.com/cloud/raw/upload/v123/folder/name.pdf  → folder/name.pdf
 */
export function getPublicIdFromCloudinaryUrl(url: string): string | null {
  try {
    // For raw resources the extension is part of the public_id, for images it's not
    const rawMatch = url.match(/\/raw\/upload\/(?:v\d+\/)?(.+)$/);
    if (rawMatch) return rawMatch[1];
    const imageMatch = url.match(/\/(?:image|video)\/upload\/(?:v\d+\/)?(.+?)(?:\.\w+)?$/);
    return imageMatch ? imageMatch[1] : null;
  } catch {
    return null;
  }
}

/**
 * Build an optimized Cloudinary URL with automatic format, quality and optional width.
 * Works only on Cloudinary URLs — returns original URL unchanged for others.
 */
export function getCloudinaryOptimizedUrl(
  url: string,
  width?: number
): string {
  if (!isCloudinaryUrl(url)) return url;
  try {
    const transforms = ['f_auto', 'q_auto', ...(width ? [`w_${width}`] : [])].join(',');
    return url.replace('/upload/', `/upload/${transforms}/`);
  } catch {
    return url;
  }
}
